import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight, CalendarSearch, ChevronLeft, ChevronRight, Info, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import OfferStrip from '../components/common/OfferStrip';
import CalendarStatCards from '../components/calendar/CalendarStatCards';
import CalendarToolbar from '../components/calendar/CalendarToolbar';
import TimelineView from '../components/calendar/TimelineView';
import MonthGridView from '../components/calendar/MonthGridView';
import PipelineView from '../components/calendar/PipelineView';
import MonthMatrixCard from '../components/calendar/MonthMatrixCard';
import CalendarFaq from '../components/calendar/CalendarFaq';
import { UpiCutoffCard } from '../components/calendar/CalendarSidebarCards';
import AdSlot from '../components/advertisement/AdSlot';
import { fetchLiveIpos } from '../store/slices/ipoSlice';
import { liveIpoToCalendarIssue } from '../utils/ipoCatalogAdapters';
import {
  buildCalendarEvents,
  buildIcsCalendar,
  countByType,
  findRushDays,
  formatDayHeading,
  formatDayShort,
  formatMonthLabel,
  getIssuePhase,
  groupEventsByDay,
  monthKeyOf,
  shiftMonth,
  todayIso,
} from '../utils/ipoCalendar';

// No backend source for day-level commentary ("Retail & institutional
// deadline for two mainboard issues") -- the old mock keyed these to fixed
// calendar dates, which could coincidentally land on a real event date and
// attach fabricated commentary to it. Left empty rather than risk that.
const DAY_NOTES = {};

const NOTICE_MS = 5000;

// Days whose count (by `pick`) is the month's highest, if it reaches `min`
const topDays = (events, pick, min) => {
  const counts = new Map();
  events.filter(pick).forEach((event) => counts.set(event.date, (counts.get(event.date) || 0) + 1));
  const max = Math.max(0, ...counts.values());
  return max >= min ? [...counts.entries()].filter(([, count]) => count === max).map(([date]) => date) : [];
};

const eventsByDate = (events) =>
  events.reduce((map, event) => map.set(event.date, [...(map.get(event.date) || []), event]), new Map());

const downloadCalendarFile = (events, filename) => {
  const blob = new Blob([buildIcsCalendar(events)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const IpoCalendarPage = () => {
  const dispatch = useDispatch();
  const today = useMemo(() => todayIso(), []);
  const [monthKey, setMonthKey] = useState(() => monthKeyOf(today));
  const [view, setView] = useState('timeline');
  const [activeTypes, setActiveTypes] = useState([]);
  const [board, setBoard] = useState('all');
  const [scrollRequest, setScrollRequest] = useState(null);
  const [notice, setNotice] = useState(null);

  const liveIposCurrent = useSelector((state) => state.ipo.liveIposCurrent);
  const liveIposUpcoming = useSelector((state) => state.ipo.liveIposUpcoming);
  const liveIposLoading = useSelector((state) => state.ipo.liveIposLoading);

  useEffect(() => {
    if (liveIposCurrent.length === 0 && liveIposUpcoming.length === 0 && !liveIposLoading) {
      dispatch(fetchLiveIpos());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Real bid-schedule data (GET /api/market/ipos/live) -- the only source
  // that reliably has open/close dates right now, see utils/ipoCatalogAdapters.js
  const calendarIssues = useMemo(
    () =>
      [...liveIposCurrent, ...liveIposUpcoming]
        .map(liveIpoToCalendarIssue)
        .filter((issue) => issue.openOn && issue.closeOn),
    [liveIposCurrent, liveIposUpcoming]
  );
  const ALL_EVENTS = useMemo(() => buildCalendarEvents(calendarIssues, []), [calendarIssues]);
  const MONTHS_WITH_EVENTS = useMemo(
    () => [...new Set(ALL_EVENTS.map((event) => monthKeyOf(event.date)))].sort(),
    [ALL_EVENTS]
  );

  const monthLabel = formatMonthLabel(monthKey);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `IPO Calendar ${monthLabel} — Mainboard & SME Dates | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, [monthLabel]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  // Scroll to a day picked from the matrix or grid once the timeline has rendered it
  useEffect(() => {
    if (!scrollRequest || view !== 'timeline') return;
    document.getElementById(`day-${scrollRequest.iso}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollRequest, view]);

  const monthEvents = useMemo(() => ALL_EVENTS.filter((event) => monthKeyOf(event.date) === monthKey), [ALL_EVENTS, monthKey]);

  const boardEvents = useMemo(
    () => monthEvents.filter((event) => event.type === 'holiday' || board === 'all' || event.issue.board === board),
    [monthEvents, board]
  );

  const visibleEvents = useMemo(
    () => (activeTypes.length ? boardEvents.filter((event) => activeTypes.includes(event.type)) : boardEvents),
    [boardEvents, activeTypes]
  );

  const days = useMemo(() => groupEventsByDay(visibleEvents), [visibleEvents]);

  const matrixEvents = useMemo(() => eventsByDate(boardEvents), [boardEvents]);
  const gridEvents = useMemo(() => eventsByDate(visibleEvents), [visibleEvents]);

  const issueEvents = useMemo(() => boardEvents.filter((event) => event.type !== 'holiday'), [boardEvents]);

  const stats = useMemo(() => {
    const allotments = issueEvents.filter((event) => event.type === 'allotment');
    const rush = findRushDays(issueEvents);
    const openings = issueEvents.filter((event) => event.type === 'open');
    return {
      total: issueEvents.length,
      opens: openings.length,
      openSizeCr: openings.reduce((sum, event) => sum + (event.issue.sizeCr || 0), 0),
      closes: issueEvents.filter((event) => event.type === 'close').length,
      allotmentsDone: allotments.filter((event) => event.date <= today).length,
      allotmentsPending: allotments.filter((event) => event.date > today).length,
      registrars: new Set(allotments.map((event) => event.issue.registrar).filter(Boolean)).size,
      rushLabel: rush.dates.slice(0, 2).map(formatDayShort).join(' & ') + (rush.dates.length > 2 ? ` +${rush.dates.length - 2}` : ''),
      rushCount: rush.count,
      rushDays: rush.dates.length,
    };
  }, [issueEvents, today]);

  const highlights = useMemo(
    () => ({
      busiest: topDays(issueEvents, () => true, 5),
      mega: topDays(issueEvents, (event) => event.type === 'open', 4),
    }),
    [issueEvents]
  );

  const boardCounts = useMemo(() => {
    const issues = new Map();
    monthEvents.forEach((event) => event.issue && issues.set(event.issue.id, event.issue.board));
    const boards = [...issues.values()];
    return {
      all: boards.length,
      mainboard: boards.filter((b) => b === 'mainboard').length,
      sme: boards.filter((b) => b === 'sme').length,
    };
  }, [monthEvents]);

  const pipelineIssues = useMemo(() => {
    const issues = new Map();
    issueEvents.forEach((event) => issues.set(event.issue.id, event.issue));
    return [...issues.values()]
      .sort((a, b) => a.closeOn.localeCompare(b.closeOn))
      .map((issue) => ({ issue, phase: getIssuePhase(issue, today) }));
  }, [issueEvents, today]);

  const upcomingVisible = visibleEvents.filter((event) => event.date >= today);

  const toggleType = (type) =>
    setActiveTypes((current) => (current.includes(type) ? current.filter((t) => t !== type) : [...current, type]));

  const changeMonth = (delta) => {
    setMonthKey((current) => shiftMonth(current, delta));
    setScrollRequest(null);
  };

  const selectDay = (iso) => {
    setActiveTypes([]);
    setView('timeline');
    setScrollRequest({ iso, at: Date.now() });
  };

  const syncEvents = (events, filename) => {
    downloadCalendarFile(events, filename);
    const single = events.length === 1;
    setNotice(
      `${events.length} event${single ? '' : 's'} saved to ${filename}. Open the file to add ${single ? 'it' : 'them'} to your calendar.`
    );
  };

  const handleSyncIssue = (issue) =>
    syncEvents(
      ALL_EVENTS.filter((event) => event.issue?.id === issue.id && event.date >= today),
      `${issue.id}-ipo.ics`
    );

  const handleAction = ({ kind, event, issue: issueArg }) => {
    const issue = issueArg || event?.issue;
    if (kind === 'apply') {
      setNotice(
        `Apply for ${issue.company} through your broker app (UPI) or net-banking ASBA before 5:00 PM IST on ${formatDayHeading(issue.closeOn)}.`
      );
    } else if (kind === 'allotment') {
      setNotice(`${issue.company} allotment status is published by ${issue.registrar || 'the registrar'}. Check it with your PAN or application number.`);
    } else if (kind === 'prospectus') {
      setNotice(`The ${issue.company} prospectus will be available once filings are connected.`);
    }
  };

  let content;
  if (!monthEvents.length) {
    const fallbackMonth = MONTHS_WITH_EVENTS[MONTHS_WITH_EVENTS.length - 1];
    content = (
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm px-6 py-14 text-center">
        <CalendarSearch className="w-10 h-10 mx-auto text-slate-300" />
        <p className="mt-3 text-sm font-bold text-slate-800">No IPO events on the calendar for {monthLabel} yet</p>
        <p className="mt-1 text-xs text-slate-500">
          {liveIposLoading
            ? 'Loading the live NSE issue calendar…'
            : 'New issues appear here once their bidding dates are announced.'}
        </p>
        {fallbackMonth && (
          <button
            type="button"
            onClick={() => setMonthKey(fallbackMonth)}
            className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#008080] hover:bg-[#006666] text-white text-xs font-bold"
          >
            Go to {formatMonthLabel(fallbackMonth)}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  } else if (view === 'pipeline') {
    content = <PipelineView issues={pipelineIssues} today={today} onSyncIssue={handleSyncIssue} onAction={handleAction} />;
  } else if (!visibleEvents.length) {
    content = (
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm px-6 py-14 text-center">
        <p className="text-sm font-bold text-slate-800">No events match these filters</p>
        <button
          type="button"
          onClick={() => {
            setActiveTypes([]);
            setBoard('all');
          }}
          className="mt-3 text-xs font-bold text-[#008080] hover:underline"
        >
          Show all events
        </button>
      </div>
    );
  } else if (view === 'grid') {
    content = <MonthGridView monthKey={monthKey} eventsByDate={gridEvents} today={today} onSelectDay={selectDay} />;
  } else {
    content = (
      <TimelineView
        days={days}
        today={today}
        dayNotes={DAY_NOTES}
        highlights={highlights}
        selectedDay={scrollRequest?.iso}
        onSync={(event) => syncEvents([event], `${event.issue ? event.issue.id : 'holiday'}-${event.type}.ics`)}
        onAction={handleAction}
      />
    );
  }

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        {/* Partner ad (GET /api/ads): page slot first, then the home leaderboard */}
        <OfferStrip slotId={['ipo-calendar-top', 'leaderboard']} />

        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          {/* Breadcrumb + header */}
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/ipo-dashboard" className="hover:text-[#008080] transition-colors">
                IPO Dashboard
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-900 font-semibold">{monthLabel} Calendar</span>
            </nav>

            <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">IPO Calendar {monthLabel}</h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] font-bold text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {stats.total} Events Active
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Track bidding timelines, opening and closing cut-offs, registrar allotment announcements and listing days
                  for every Mainboard and SME IPO in India.
                </p>
              </div>

              <nav aria-label="Change month" className="inline-flex items-center self-start lg:self-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => changeMonth(-1)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{formatMonthLabel(shiftMonth(monthKey, -1))}</span>
                  <span className="sm:hidden">{formatMonthLabel(shiftMonth(monthKey, -1), { short: true })}</span>
                </button>
                <span className="px-3 py-1.5 rounded-lg bg-teal-50 text-xs font-extrabold text-[#008080] whitespace-nowrap" aria-current="date">
                  {monthLabel}
                </span>
                <button
                  type="button"
                  onClick={() => changeMonth(1)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100"
                >
                  <span className="hidden sm:inline">{formatMonthLabel(shiftMonth(monthKey, 1))}</span>
                  <span className="sm:hidden">{formatMonthLabel(shiftMonth(monthKey, 1), { short: true })}</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </nav>
            </div>
          </div>

          <CalendarStatCards stats={stats} />

          <CalendarToolbar
            view={view}
            onViewChange={setView}
            typeCounts={countByType(boardEvents)}
            totalCount={boardEvents.length}
            activeTypes={activeTypes}
            onToggleType={toggleType}
            onClearTypes={() => setActiveTypes([])}
            board={board}
            onBoardChange={setBoard}
            boardCounts={boardCounts}
            onSyncAll={() => syncEvents(upcomingVisible, `ipo-calendar-${monthKey}.ics`)}
            canSync={upcomingVisible.length > 0}
          />

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 sm:gap-6 items-start">
            <div className="min-w-0">{content}</div>
            <aside className="space-y-5 sm:space-y-6" aria-label="Calendar tools">
              <MonthMatrixCard monthKey={monthKey} eventsByDate={matrixEvents} today={today} onSelectDay={selectDay} />
              <UpiCutoffCard />
              {/* Sidebar ads (GET /api/ads): dedicated calendar slots first, then the shared ones */}
              <AdSlot slotId={['calendar-sidebar-top', 'incontent']} variant="compact" />
              <AdSlot slotId={['calendar-sidebar-bottom', 'infeed']} variant="compact" />
            </aside>
          </div>

          <CalendarFaq />
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default IpoCalendarPage;
